import { useEffect, useState } from "react";
import { useCachedState } from "@/hooks/use-cached-state";
import { createFileRoute } from "@tanstack/react-router";
import { TableSkeleton } from "@/components/loading-skeletons";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader } from "@/components/page-header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Inbox } from "lucide-react";
import { toast } from "sonner";
import { ImageUpload } from "@/components/image-upload";
import { useStorageUrl } from "@/components/storage-image";

export const Route = createFileRoute("/_authenticated/store/banners")({
  head: () => ({ meta: [{ title: "Banner Store — MI Al-Munajah Admin" }] }),
  component: BannersPage,
});

type Banner = {
  id: string;
  title: string | null;
  subtitle: string | null;
  image_url: string;
  product_id: string | null;
  sort_order: number;
  is_active: boolean;
};
type ProductOpt = { id: string; name: string };

const empty = { title: "", subtitle: "", image_url: "", product_id: "", sort_order: 0, is_active: true };

function BannersPage() {
  const [rows, setRows] = useCachedState<Banner[]>("store.banners:rows", []);
  const [products, setProducts] = useCachedState<ProductOpt[]>("store.banners:products", []);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Banner | null>(null);
  const [form, setForm] = useState(empty);
  const [delId, setDelId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    const [{ data: b }, { data: p }] = await Promise.all([
      supabase.from("store_banners").select("*").order("sort_order"),
      supabase.from("store_products").select("id,name").order("name"),
    ]);
    setRows((b ?? []) as Banner[]);
    setProducts((p ?? []) as ProductOpt[]);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (r: Banner) => {
    setEditing(r);
    setForm({
      title: r.title ?? "", subtitle: r.subtitle ?? "",
      image_url: r.image_url, product_id: r.product_id ?? "",
      sort_order: r.sort_order, is_active: r.is_active,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.image_url) return toast.error("Gambar wajib diisi");
    setBusy(true);
    const payload = {
      title: form.title.trim() || null,
      subtitle: form.subtitle.trim() || null,
      image_url: form.image_url,
      product_id: form.product_id || null,
      sort_order: Number(form.sort_order) || 0,
      is_active: form.is_active,
    };
    const { error } = editing
      ? await supabase.from("store_banners").update(payload).eq("id", editing.id)
      : await supabase.from("store_banners").insert(payload);
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success(editing ? "Banner diperbarui" : "Banner dibuat");
    setOpen(false);
    load();
  };

  const remove = async () => {
    if (!delId) return;
    setBusy(true);
    const { error } = await supabase.from("store_banners").delete().eq("id", delId);
    setBusy(false);
    setDelId(null);
    if (error) return toast.error("Gagal menghapus: " + error.message);
    toast.success("Banner dihapus");
    load();
  };

  return (
    <div>
      <PageHeader
        title="Banner Store"
        description="Banner yang tampil di halaman utama katalog aplikasi Android."
        actions={<Button onClick={openCreate}><Plus className="w-4 h-4" /> Tambah Banner</Button>}
      />
      <Card className="p-4 rounded-2xl">
        <div className="rounded-xl border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Preview</TableHead>
                <TableHead>Judul</TableHead>
                <TableHead>Produk</TableHead>
                <TableHead className="text-right">Urutan</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableSkeleton columns={6} />
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="py-12">
                  <div className="flex flex-col items-center text-muted-foreground gap-2">
                    <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center"><Inbox className="w-6 h-6" /></div>
                    <div className="font-medium text-foreground">Belum ada banner</div>
                  </div>
                </TableCell></TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell><BannerThumb path={r.image_url} /></TableCell>
                  <TableCell className="font-medium">
                    {r.title || "—"}
                    {r.subtitle && <div className="text-xs text-muted-foreground">{r.subtitle}</div>}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {products.find((p) => p.id === r.product_id)?.name ?? "—"}
                  </TableCell>
                  <TableCell className="text-right">{r.sort_order}</TableCell>
                  <TableCell>
                    {r.is_active
                      ? <Badge variant="outline" className="bg-success/15 text-success border-success/30">Aktif</Badge>
                      : <Badge variant="outline" className="bg-muted text-muted-foreground">Nonaktif</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(r)}><Pencil className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => setDelId(r.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Banner" : "Tambah Banner"}</DialogTitle>
            <DialogDescription>Banner promosi yang tampil pada halaman utama Toko Sekolah.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Gambar Banner <span className="text-destructive">*</span></Label>
              <ImageUpload bucket="store" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v ?? "" })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Judul</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </div>
              <div>
                <Label>Sub Judul</Label>
                <Input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Tautkan ke Produk (opsional)</Label>
              <Select value={form.product_id || "none"} onValueChange={(v) => setForm({ ...form, product_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Tidak ada" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Tidak ada</SelectItem>
                  {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <div>
                <Label>Urutan</Label>
                <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} />
              </div>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div className="text-sm font-medium">Aktif</div>
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} disabled={busy}>{editing ? "Simpan" : "Tambah"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus banner?</AlertDialogTitle>
            <AlertDialogDescription>Tindakan ini tidak dapat dibatalkan.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={remove} disabled={busy} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function BannerThumb({ path }: { path: string }) {
  const url = useStorageUrl("store", path);
  if (!url) return <div className="w-20 h-12 rounded bg-muted" />;
  return <img src={url} alt="" className="w-20 h-12 rounded object-cover border" />;
}
